import { format } from 'date-fns';

/**
 * Get the appropriate color for an appointment status
 * @param {string} status - The appointment status
 * @returns {string} - The MUI color name
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'confirmed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'error';
    case 'rescheduled':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Format a date range for display
 * @param {Date} start - The start date
 * @param {Date} end - The end date
 * @returns {string} - Formatted date range
 */
export const formatDateRange = (start, end) => {
  if (!start || !end) return 'Invalid date range';

  return `${format(start, 'MMM d, yyyy')} (${format(start, 'h:mm a')} - ${format(end, 'h:mm a')})`;
};

/**
 * Calculate appointment duration in minutes
 * @param {Date} start - The start date
 * @param {Date} end - The end date
 * @returns {number} - Duration in minutes
 */
export const calculateDuration = (start, end) => {
  if (!start || !end) return 0;

  return Math.round((end - start) / (1000 * 60));
};

/**
 * Format a page title based on date and hour
 * @param {Date} date - The date
 * @param {number} hour - The hour
 * @returns {string} - Formatted page title
 */
export const formatAppointmentPageTitle = (date, hour) => {
  if (date && hour !== null) {
    return `Appointments for ${format(date, 'EEEE, MMMM d, yyyy')} at ${format(new Date().setHours(hour, 0, 0), 'h:mm a')}`;
  } else if (date) {
    return `Appointments for ${format(date, 'EEEE, MMMM d, yyyy')}`;
  } else {
    return 'All Appointments';
  }
};

/**
 * Get a human-readable status label
 * @param {string} status - The appointment status
 * @returns {string} - Human-readable status
 */
export const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'confirmed':
      return 'Confirmed';
    case 'pending':
      return 'Pending';
    case 'cancelled':
      return 'Cancelled';
    case 'rescheduled':
      return 'Rescheduled';
    default:
      return 'Scheduled';
  }
};

/**
 * Get available status options for filtering
 * @returns {Array} - Array of status options
 */
export const getStatusOptions = () => [
  { value: 'all', label: 'All Statuses' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' }
];
